import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/data/products";
import { X, Minus, Plus } from "lucide-react";
import { Link } from "react-router-dom";

export default function CartDrawer() {
  const { items, open, setOpen, update, remove, subtotal } = useCart();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-ivory border-l border-border p-0 flex flex-col">
        <SheetHeader className="px-8 pt-8 pb-4 flex-row items-center justify-between space-y-0">
          <SheetTitle className="overline font-medium">Shopping Bag ({items.length})</SheetTitle>
        </SheetHeader>
        <div className="divider-thin mx-8" />

        <div className="flex-1 overflow-y-auto px-8 py-6">
          {items.length === 0 ? (
            <div className="text-center pt-16">
              <p className="font-serif text-2xl tracking-tighter">Your bag is empty</p>
              <p className="mt-3 text-sm text-muted-foreground">Discover the season's couture edits.</p>
              <button onClick={() => setOpen(false)} className="lux-btn-outline mt-8">Continue</button>
            </div>
          ) : items.map((i, idx) => (
            <div key={idx} className="flex gap-5 py-6 border-b border-border" data-testid={`cart-item-${idx}`}>
              <div className="w-24 h-32 bg-muted overflow-hidden flex-shrink-0">
                <img src={i.image} alt={i.title} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1">
                <p className="font-serif text-lg tracking-tight">{i.title}</p>
                <p className="text-xs text-muted-foreground mt-1">{i.subtitle}</p>
                {i.size && <p className="overline mt-2 text-muted-foreground">Size · {i.size}</p>}
                <div className="flex items-center justify-between mt-4">
                  <div className="flex items-center gap-2 border border-border">
                    <button onClick={() => update(idx, i.quantity - 1)} className="p-2"><Minus size={12} strokeWidth={1} /></button>
                    <span className="text-sm w-6 text-center" data-testid={`cart-qty-${idx}`}>{i.quantity}</span>
                    <button onClick={() => update(idx, i.quantity + 1)} className="p-2"><Plus size={12} strokeWidth={1} /></button>
                  </div>
                  <p className="text-sm">{formatPrice(i.price * i.quantity)}</p>
                </div>
              </div>
              <button data-testid={`cart-remove-${idx}`} onClick={() => remove(idx)} className="text-muted-foreground self-start"><X size={16} strokeWidth={1} /></button>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="border-t border-border p-8 space-y-4 bg-ivory">
            <div className="flex justify-between text-sm">
              <span className="overline">Subtotal</span>
              <span data-testid="cart-subtotal" className="font-serif text-xl">{formatPrice(subtotal)}</span>
            </div>
            <p className="text-xs text-muted-foreground">Complimentary shipping. Taxes calculated at checkout.</p>
            <Link to="/checkout" onClick={() => setOpen(false)} className="lux-btn-dark w-full" data-testid="drawer-checkout">Checkout</Link>
            <Link to="/cart" onClick={() => setOpen(false)} className="lux-btn-outline w-full" data-testid="drawer-view-bag">View Bag</Link>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
